// src/MainApp.js
import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabaseClient";
import toast, { Toaster } from "react-hot-toast";

import Market from "./Market";
import Portfolio from "./Portfolio";
import Leaderboard from "./Leaderboard";
import Settings from "./Settings";
import Leagues from "./Leagues";
import { updatePrices } from "./priceUpdater";
import { updateStrengthRatings } from "./UpdateStrengthRatings";
import { STARTING_CASH } from "./User";

const ADMIN_EMAIL = "wonof1sports@gmail.com";

// Links & Contact
const YOUTUBE_URL = "https://youtube.com/@wonof1?si=AoKrUxiPGBsQieEo";
const KOFI_URL = "https://ko-fi.com/wonof1";
const REPORT_EMAIL = "wonof1sports@gmail.com";

function MainApp({ user }) {
  const [currentPage, setCurrentPage] = useState("Market");
  const [teams, setTeams] = useState([]);
  const [userData, setUserData] = useState(null);
  const [marketOpen, setMarketOpen] = useState(true);
  const [marketStats, setMarketStats] = useState({ mostBought: [], mostSold: [] });

  const isAdmin = user && user.email === ADMIN_EMAIL;

  // 1. Listen to market open/closed status in Supabase
  useEffect(() => {
    const fetchMarketStatus = async () => {
      const { data, error } = await supabase
        .from("config")
        .select("is_open")
        .eq("id", "market")
        .maybeSingle();

      if (!error && data && data.is_open !== undefined) {
        setMarketOpen(data.is_open);
      } else {
        setMarketOpen(true);
      }
    };

    fetchMarketStatus();

    const channel = supabase
      .channel("public:config")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "config", filter: "id=eq.market" },
        (payload) => {
          if (payload.new && payload.new.is_open !== undefined) {
            setMarketOpen(payload.new.is_open);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // 2. Listen to teams table in real-time in Supabase
  useEffect(() => {
    const fetchTeams = async () => {
      const { data, error } = await supabase.from("teams").select("*");
      if (error) {
        console.error("Error fetching teams:", error);
      } else if (data) {
        const formatted = data.map((t) => {
          const teamLogo = t.logo_url || t.logoUrl || t.logo || "";
          return {
            ...t,
            logo: teamLogo,
            logoUrl: teamLogo,
            logo_url: teamLogo,
            buy_price: t.buy_price ?? t.price ?? 10.0,
            sell_price: t.sell_price ?? t.price ?? 10.0,
            price: t.price ?? 10.0, 
            availableShares: t.available_shares ?? t.availableShares ?? 1000,
            priceHistory: t.price_history ?? t.priceHistory ?? [],
          };
        });
        setTeams(formatted);
      }
    };

    fetchTeams();

    const teamsChannel = supabase
      .channel("public:teams")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "teams" },
        () => {
          fetchTeams();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(teamsChannel);
    };
  }, []);

  // 3. Listen to current user profile & cash in Supabase
  useEffect(() => {
    if (!user) return;

    const userId = user.id || user.uid;

    const fetchProfile = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error || !data) {
        const initialData = {
          id: userId,
          username:
            user.user_metadata?.username ||
            user.displayName ||
            user.email.split("@")[0],
          email: user.email,
          cash: STARTING_CASH,
          portfolio: {},
        };
        await supabase.from("profiles").upsert(initialData);
        setUserData(initialData);
      } else {
        setUserData(data);
      }
    };

    fetchProfile();

    const profileChannel = supabase
      .channel(`public:profiles:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "profiles", filter: `id=eq.${userId}` },
        (payload) => {
          if (payload.new) setUserData(payload.new);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(profileChannel);
    };
  }, [user]);

  // 4. Fetch and listen to transactions for "Most Bought" / "Most Sold" stats
  useEffect(() => {
    const fetchStats = async () => {
      const { data: transactions, error } = await supabase
        .from("transactions")
        .select("team_name, type");

      if (error || !transactions) return;

      const counts = transactions.reduce((acc, tx) => {
        if (!acc[tx.team_name]) acc[tx.team_name] = { BUY: 0, SELL: 0 };
        if (tx.type === "BUY" || tx.type === "SELL") {
          acc[tx.team_name][tx.type]++;
        }
        return acc;
      }, {});

      const mostBought = Object.entries(counts)
        .map(([name, types]) => ({ name, count: types.BUY }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      const mostSold = Object.entries(counts)
        .map(([name, types]) => ({ name, count: types.SELL }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setMarketStats({ mostBought, mostSold });
    };

    fetchStats();

    const txChannel = supabase
      .channel("public:transactions")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "transactions" },
        fetchStats
      )
      .subscribe();

    return () => {
      supabase.removeChannel(txChannel);
    };
  }, []);

  const cash = userData?.cash ?? STARTING_CASH;
  const userPortfolio = useMemo(() => userData?.portfolio || {}, [userData]);

  const teamsWithShares = useMemo(() => {
    return teams.map((team) => ({
      ...team,
      shares: userPortfolio[team.id] || 0,
    }));
  }, [teams, userPortfolio]);

  const handleShareApp = async () => {
    const shareData = {
      title: "WonOf1 CFB Stock Market",
      text: "Trade college football stocks and compete in private leagues!",
      url: window.location.origin,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {}
    } else {
      navigator.clipboard.writeText(window.location.origin);
      toast.success("App link copied to clipboard!");
    }
  };

  const syncUpdatedPricesToSupabase = async (updatedTeams) => {
    try {
      for (const team of updatedTeams) {
        const oldTeam = teams.find((t) => t.id === team.id);
        if (oldTeam && (oldTeam.buy_price !== team.buy_price || oldTeam.sell_price !== team.sell_price)) {
          const { error } = await supabase
            .from("teams")
            .update({
              price: team.price,
              buy_price: team.buy_price,
              sell_price: team.sell_price,
              price_history: team.priceHistory || team.price_history || [],
            })
            .eq("id", team.id);

          if (error) {
            console.error("Supabase team update error for team", team.name, ":", error.message);
          }
        }
      }

      await updateStrengthRatings();
    } catch (error) {
      console.error("Failed to sync price updates:", error);
    }
  };

  // Add Cash Handler for Get More Money Button
  const handleAddCash = async (amount = 500) => {
    try {
      const userId = user.id || user.uid;
      const newCash = cash + amount;

      const { error } = await supabase
        .from("profiles")
        .update({ cash: newCash })
        .eq("id", userId);

      if (error) throw error;

      setUserData((prev) => ({ ...prev, cash: newCash }));
      toast.success(`Successfully added $${amount} to your cash balance!`);
    } catch (error) {
      console.error("Error adding cash:", error);
      toast.error(`Failed to add cash: ${error.message}`);
    }
  };

  // Buy Shares Execution
  const buyShares = async (teamId, quantity) => {
    if (!marketOpen) return toast.error("Market is currently closed.");

    quantity = Number(quantity);
    if (quantity <= 0 || isNaN(quantity)) return;

    try {
      const userId = user.id || user.uid;
      const targetTeam = teams.find((t) => t.id === teamId);
      if (!targetTeam) throw new Error("Team not found.");

      const buyPrice = targetTeam.buy_price ?? targetTeam.price ?? 10.0;
      const cost = buyPrice * quantity;
      
      if (cost > cash) throw new Error("Not enough cash!");

      const userSharesOwned = userPortfolio[teamId] || 0;
      const maxAvailable = targetTeam.availableShares || 1000;
      if (quantity > maxAvailable) {
        throw new Error("Not enough shares available!");
      }

      const updatedTeams = updatePrices(teams, { [teamId]: quantity }, []);
      const newShares = userSharesOwned + quantity;
      const newCash = cash - cost;
      const updatedPortfolio = { ...userPortfolio, [teamId]: newShares };
      const newAvailableShares = Math.max(0, maxAvailable - quantity);

      const { error: profileError } = await supabase
        .from("profiles")
        .update({ cash: newCash, portfolio: updatedPortfolio })
        .eq("id", userId);

      if (profileError) throw profileError;

      const { error: teamUpdateError } = await supabase
        .from("teams")
        .update({ available_shares: newAvailableShares })
        .eq("id", teamId);

      if (teamUpdateError) throw teamUpdateError;

      const { error: txError } = await supabase.from("transactions").insert([
        {
          user_id: userId,
          team_id: targetTeam.id,
          team_name: targetTeam.name,
          type: "BUY",
          quantity: quantity,
          price: buyPrice,
          total: cost,
          timestamp: new Date().toISOString(),
        },
      ]);

      if (txError) console.error("Error inserting transaction log:", txError);

      // Sync and Update UI
      await syncUpdatedPricesToSupabase(updatedTeams);
      setTeams(updatedTeams); 
      setUserData((prev) => ({ ...prev, cash: newCash, portfolio: updatedPortfolio }));
      toast.success(`Bought ${quantity} share(s) of ${targetTeam.name}!`);
    } catch (error) {
      console.error("Buy error:", error);
      toast.error(`Transaction failed: ${error.message}`);
    }
  };

  // Sell Shares Execution
  const sellShares = async (teamId, quantity) => {
    if (!marketOpen) return toast.error("Market is currently closed.");

    quantity = Number(quantity);
    if (quantity <= 0 || isNaN(quantity)) return;

    try {
      const userId = user.id || user.uid;
      const userSharesOwned = userPortfolio[teamId] || 0;

      if (quantity > userSharesOwned) {
        throw new Error("You cannot sell more shares than you own!");
      }

      const targetTeam = teams.find((t) => t.id === teamId);
      if (!targetTeam) throw new Error("Team not found.");

      const sellPrice = targetTeam.sell_price ?? targetTeam.price ?? 10.0;
      const revenue = sellPrice * quantity;
      
      const updatedTeams = updatePrices(teams, { [teamId]: -quantity }, []);
      const newShares = userSharesOwned - quantity;
      const newCash = cash + revenue;

      const updatedPortfolio = { ...userPortfolio };
      if (newShares > 0) {
        updatedPortfolio[teamId] = newShares;
      } else {
        delete updatedPortfolio[teamId];
      }

      const currentAvailable = targetTeam.availableShares || 1000;
      const newAvailableShares = Math.min(1000, currentAvailable + quantity);

      const { error: profileError } = await supabase
        .from("profiles")
        .update({ cash: newCash, portfolio: updatedPortfolio })
        .eq("id", userId);

      if (profileError) throw profileError;

      const { error: teamUpdateError } = await supabase
        .from("teams")
        .update({ available_shares: newAvailableShares })
        .eq("id", teamId);

      if (teamUpdateError) throw teamUpdateError;

      const { error: txError } = await supabase.from("transactions").insert([
        {
          user_id: userId,
          team_id: targetTeam.id,
          team_name: targetTeam.name,
          type: "SELL",
          quantity: quantity,
          price: sellPrice,
          total: revenue,
          timestamp: new Date().toISOString(),
        },
      ]);

      if (txError) console.error("Error inserting transaction log:", txError);

      // Sync and Update UI
      await syncUpdatedPricesToSupabase(updatedTeams);
      setTeams(updatedTeams);
      setUserData((prev) => ({ ...prev, cash: newCash, portfolio: updatedPortfolio }));
      toast.success(`Sold ${quantity} share(s) of ${targetTeam.name}!`);
    } catch (error) {
      console.error("Sell error:", error);
      toast.error(`Transaction failed: ${error.message}`);
    }
  };

  // Immediate Game Result Handler
  const addGameResult = async (
    winnerId,
    loserId,
    winnerScore = 0,
    loserScore = 0,
    customStrengthOverride = null
  ) => {
    if (!winnerId || !loserId || winnerId === loserId) return;

    const margin = Math.max(1, Math.abs(Number(winnerScore) - Number(loserScore)));
    const parsedOverride =
      customStrengthOverride !== null && customStrengthOverride !== ""
        ? Number(customStrengthOverride)
        : null;

    const gameResultObj = {
      winnerId,
      loserId,
      margin,
      customStrengthOverride: parsedOverride,
    };

    try {
      const updatedTeams = updatePrices(teams, {}, [gameResultObj]);
      await syncUpdatedPricesToSupabase(updatedTeams);
      setTeams(updatedTeams); // Update UI Immediately
      toast.success("Game result recorded and prices updated!");
    } catch (error) {
      toast.error(`Failed to record game result: ${error.message}`);
    }
  };

  const resetSeasonData = async () => {
    if (!isAdmin) return;
    const confirmReset = window.confirm(
      "Are you sure you want to reset the entire season? This will restore all team prices to $10.00, restore share supplies, and reset all user cash balances to $10,000!"
    );
    if (!confirmReset) return;

    try {
      await supabase.from("teams").update({
        price: 10.0,
        buy_price: 10.0,
        sell_price: 10.0,
        available_shares: 1000,
        price_history: [{ date: new Date().toISOString(), price: 10.0 }],
      }).neq("id", "0");

      await supabase.from("profiles").update({
        cash: STARTING_CASH,
        portfolio: {},
      }).neq("id", "0");

      toast.success("Season reset completed successfully!");
    } catch (error) {
      toast.error(`Season reset failed: ${error.message}`);
    }
  };

  const handleLogout = () => supabase.auth.signOut();

  const navPages = ["Market", "Portfolio", "Leaderboard", "Leagues"];
  if (isAdmin) navPages.push("Settings");

  return (
    <div style={{ padding: "20px", maxWidth: "1280px", margin: "0 auto" }}>
      <Toaster position="top-right" reverseOrder={false} />

      <div
        style={{
          background: "linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)",
          color: "white",
          borderRadius: "12px",
          padding: "1.25rem 1.5rem",
          marginBottom: "1.5rem",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "1rem",
          position: "relative",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "14px", paddingRight: "70px" }}>
          <img
            src="/wogostock.png"
            alt="WonOf1 Logo"
            style={{
              height: "48px",
              width: "auto",
              borderRadius: "8px",
              objectFit: "contain",
            }}
          />
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: "800", letterSpacing: "0.5px" }}>
                WonOf1 CFB Stock Market
              </h1>
              <span
                style={{
                  backgroundColor: marketOpen ? "#16a34a" : "#dc2626",
                  color: "white",
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {marketOpen ? "● Market Open" : "● Market Closed"}
              </span>
            </div>
            <p style={{ margin: "4px 0 0 0", color: "#93c5fd", fontSize: "0.9rem" }}>
              Welcome back, <strong>{userData?.username || user.displayName || user.email}</strong>
            </p>
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            top: "16px",
            right: "12px",
            fontSize: "2.5rem",
            background: "rgba(255, 255, 255, 0.1)",
            padding: "6px 12px",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}
        >
          🏈
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", width: "100%", marginTop: "8px" }}>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.12)",
              padding: "6px 12px",
              borderRadius: "6px",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              fontSize: "0.9rem",
              marginRight: "4px",
            }}
          >
            Cash: <strong style={{ color: "#4ade80" }}>${cash.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
          </div>

          <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" style={{ padding: "6px 10px", backgroundColor: "#ff0000", color: "white", borderRadius: "6px", textDecoration: "none", fontSize: "0.85rem", fontWeight: "bold", display: "inline-flex", alignItems: "center", gap: "4px" }}>▶ YouTube</a>
          <a href={KOFI_URL} target="_blank" rel="noopener noreferrer" style={{ padding: "6px 10px", backgroundColor: "#ff5e5b", color: "white", borderRadius: "6px", textDecoration: "none", fontSize: "0.85rem", fontWeight: "bold", display: "inline-flex", alignItems: "center", gap: "4px" }}>☕ Support</a>
          <button onClick={handleShareApp} style={{ padding: "6px 10px", backgroundColor: "#059669", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem", fontWeight: "bold" }}>🔗 Share</button>
          <a href={`mailto:${REPORT_EMAIL}?subject=CFB Stock Game - Bug Report`} style={{ padding: "6px 10px", backgroundColor: "#4b5563", color: "white", borderRadius: "6px", textDecoration: "none", fontSize: "0.85rem", fontWeight: "bold" }}>⚠️ Report Bug</a>
          <button onClick={handleLogout} style={{ padding: "6px 10px", backgroundColor: "#dc2626", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem", fontWeight: "bold" }}>Logout</button>
        </div>
      </div>

      <nav style={{ marginBottom: "20px" }}>
        {navPages.map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            style={{
              marginRight: "8px",
              backgroundColor: currentPage === page ? "#2563eb" : "#f3f4f6",
              color: currentPage === page ? "white" : "#374151",
              padding: "8px 18px",
              border: "1px solid " + (currentPage === page ? "#2563eb" : "#d1d5db"),
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: currentPage === page ? "bold" : "600",
              fontSize: "0.95rem",
            }}
          >
            {page}
          </button>
        ))}
      </nav>

      {currentPage === "Market" && (
        <Market
          teams={teamsWithShares}
          cash={cash}
          buyShares={buyShares}
          sellShares={sellShares}
          addCash={handleAddCash}
          addGameResult={addGameResult}
          marketOpen={marketOpen}
          mostBought={marketStats.mostBought}
          mostSold={marketStats.mostSold}
        />
      )}
      {currentPage === "Portfolio" && (
        <Portfolio teams={teamsWithShares} cash={cash} />
      )}
      {currentPage === "Leaderboard" && (
        <Leaderboard teams={teamsWithShares} />
      )}
      {currentPage === "Leagues" && (
        <Leagues teams={teamsWithShares} />
      )}
      {currentPage === "Settings" && isAdmin && (
        <Settings
          teams={teamsWithShares}
          addGameResult={addGameResult}
          resetSeasonData={resetSeasonData}
        />
      )}
    </div>
  );
}

export default MainApp;